"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DbGraphUtils = void 0;
const common_1 = require("@finley-technologies/common");
const pointer_methods_1 = require("@finley-technologies/pointer-methods");
const createLoanProductShell_1 = require("../createLoanProductShell");
const tapeBuilder_1 = require("../tapeBuilder");
const typeguards_1 = require("../typeguards");
// this namespace exports utilities to work with the whole graph.
// the input is artifactDocumentComplete.
var DbGraphUtils;
(function (DbGraphUtils) {
    DbGraphUtils.deserializeForBBPLMonitor = (str) => {
        const { monitorArtifacts, monitorDependencies } = JSON.parse(str);
        return {
            artifacts: monitorArtifacts,
            edges: monitorDependencies,
        };
    };
    DbGraphUtils.createGraphUtils = ({ id: artifactDocumentId, version, artifacts, cfIdToSlugMap, lpIdToSlugMap, dependencies, }) => {
        // APIS in this interface they must respect the scopedToCFSlugs.
        // if caller does not have access to a creditFacilitySlug,
        // the API must fail.
        const scopedToCFSlugs = [];
        const prefix = `[artifactDocumentId:${artifactDocumentId}]`;
        // returns direct outputs of the given artifactId
        const _getOutputs = (artifactId) => {
            return dependencies
                .filter(({ inputArtifactId }) => inputArtifactId === artifactId)
                .map(({ outputArtifactId }) => outputArtifactId);
        };
        // returns direct inputs of the given artifactId
        const _getInputs = (artifactId) => {
            return dependencies
                .filter(({ outputArtifactId }) => outputArtifactId === artifactId)
                .map(({ inputArtifactId }) => inputArtifactId);
        };
        // returns all outputs of the given artifactId
        const _getRecursiveOutputs = (artifactId) => {
            const directDependents = _getOutputs(artifactId);
            const recursiveDependents = directDependents.flatMap((dependent) => _getRecursiveOutputs(dependent));
            return Array.from(new Set([...recursiveDependents, ...directDependents]));
        };
        // returns all inputs for the given artifactId
        const _getRecursiveInputs = (artifactId) => {
            const directInputs = _getInputs(artifactId);
            const recursiveInputs = directInputs.flatMap((input) => _getRecursiveInputs(input));
            return Array.from(new Set([...directInputs, ...recursiveInputs]));
        };
        const _isRawTape = (artifactId) => {
            return _getInputs(artifactId).length === 0;
        };
        const _getArtifactById = (artifactId) => {
            const foundArtifacts = artifacts.filter((artifact) => artifact.id === artifactId);
            (0, common_1.finleyAssert)(foundArtifacts.length === 1, `${prefix}: [artifactId:${artifactId}] found [${foundArtifacts.length}] matches]`);
            return foundArtifacts[0];
        };
        // return all raw inputs required for given artifactId.
        const _getRawInputs = (artifactId) => {
            const rawInputs = _getRecursiveInputs(artifactId)
                .filter(_isRawTape)
                .map(_getArtifactById);
            return rawInputs;
        };
        const getInputArtifacts = (artifactId) => _getInputs(artifactId).map(_getArtifactById);
        const _getBBRNodes = () => {
            return artifacts
                .filter(({ type }) => type === 'borrowingBaseReport')
                .map((artifact) => {
                (0, common_1.finleyAssert)(typeguards_1.typeguards.isTapeBuilderDbRecordSyntheticBorrowingBaseReport(artifact), 'typeguard.isTapeBuilderDbRecordSyntheticBorrowingBaseReport failed');
                return artifact;
            });
        };
        const _getGeneratedLoanTapeArtifacts = ({ loanProductSlug, }) => {
            const generatedLoanTapes = artifacts
                .filter(({ type }) => type === 'generatedLoanTape')
                .filter(({ loanProductSlug: slug }) => slug === loanProductSlug);
            (0, common_1.finleyAssert)(generatedLoanTapes.length >= 0, `${prefix}: no generatedLoanTape found matching [loanProductSlug:${loanProductSlug}]`);
            return generatedLoanTapes;
        };
        // if scopedToCFSlugs is not provided, we assume that caller has access to all
        const limitScopeToCFSlugs = (cfSlugs) => {
            // copy over the array
            scopedToCFSlugs.length = 0;
            scopedToCFSlugs.push(...cfSlugs);
        };
        const cfIdToSlug = (id) => {
            const slug = cfIdToSlugMap.get(id);
            (0, common_1.finleyAssert)(slug, `${prefix}: [creditFacilityId:${id}] is not referenced in the graph`);
            return slug;
        };
        const getCFSlugs = () => Array.from(cfIdToSlugMap.values());
        const getCFIds = () => Array.from(cfIdToSlugMap.keys());
        const getLPSlugs = () => Array.from(lpIdToSlugMap.values());
        const getLPIds = () => Array.from(lpIdToSlugMap.keys());
        const lpIdToSlug = (id) => {
            const slug = lpIdToSlugMap.get(id);
            (0, common_1.finleyAssert)(slug, `${prefix}: [loanProductId:${id}] is not referenced in the graph`);
            return slug;
        };
        const lpSlugToId = (slug) => {
            const id = Array.from(lpIdToSlugMap.entries()).find(([, v]) => v === slug)?.[0];
            (0, common_1.finleyAssert)(id, `${prefix}: [loanProductSlug:${slug}] is not referenced in the graph`);
            return id;
        };
        const cfSlugToId = (slug) => {
            const id = Array.from(cfIdToSlugMap.entries()).find(([, v]) => v === slug)?.[0];
            (0, common_1.finleyAssert)(id, `${prefix}: [creditFacilitySlug:${slug}] is not referenced in the graph`);
            return id;
        };
        const assertCreditFacilityAccess = (creditFacilitySlug) => {
            (0, common_1.finleyAssert)(scopedToCFSlugs.includes(creditFacilitySlug), `${prefix}: caller does not have access to [creditFacilitySlug:${creditFacilitySlug}], [scopedToCFSlugs:${scopedToCFSlugs.join(',')}]`);
        };
        const hasRepaymentTape = (loanProductSlug) => {
            return (artifacts.filter((artifact) => artifact.type === 'generatedRepaymentTape' &&
                artifact.loanProductSlug === loanProductSlug).length > 0);
        };
        /**
         * ArtifactWithSlugs => Artifact
         */
        const toArtifact = (artifactWithSlugs) => {
            const { creditFacilitySlug, loanProductSlug, ...rest } = artifactWithSlugs;
            const creditFacilityId = creditFacilitySlug
                ? cfSlugToId(creditFacilitySlug)
                : null;
            const loanProductId = loanProductSlug
                ? lpSlugToId(loanProductSlug)
                : null;
            return {
                ...rest,
                creditFacilityId,
                loanProductId,
            };
        };
        const getAssetVerificationConfigsForCF = ({ creditFacilitySlug, }) => {
            assertCreditFacilityAccess(creditFacilitySlug);
            // find generatedInvoice node for matching params.
            const assetVerificationConfigs = artifacts
                .filter((artifact) => {
                return (artifact.type === 'receivableInvoice' &&
                    artifact.creditFacilitySlug === creditFacilitySlug);
            })
                .map((artifact) => {
                const config = artifact.configuration;
                (0, common_1.finleyAssert)(typeguards_1.typeguards.isTapeBuilderRecordGeneratedReceivableInvoiceTapeConfiguration(config), `${prefix}: expected receivableInvoice node to be of [artifactType:${artifact.type}]`);
                (0, common_1.finleyAssert)(artifact.loanProductSlug !== null, `${prefix}: loanProductSlug is required for [artifactType:${artifact.type}]`);
                const assetVerificationConfig = config.assetVerificationConfig;
                return {
                    loanProductSlug: artifact.loanProductSlug,
                    assetVerificationConfig,
                };
            });
            return assetVerificationConfigs;
        };
        const maybeGetAssetVerificationConfig = ({ loanProductSlug, creditFacilitySlug, }) => {
            assertCreditFacilityAccess(creditFacilitySlug);
            // find generatedInvoice node for matching params.
            const matchingConfigs = getAssetVerificationConfigsForCF({
                creditFacilitySlug,
            }).filter((config) => config.loanProductSlug === loanProductSlug);
            if (matchingConfigs.length === 0) {
                // its possible that CF/LP is not configured for asset verification.
                return null;
            }
            // we must find only 1 matching config
            (0, common_1.finleyAssert)(matchingConfigs.length === 1, `${prefix}: found [multiple:${matchingConfigs.length}] assetVerificationConfigs for [loanProductSlug:${loanProductSlug}] and [creditFacilitySlug:${creditFacilitySlug}]`);
            return matchingConfigs[0].assetVerificationConfig;
        };
        // TODO(jon): lot of assumptions to clean up in this method. This really only works for Atalaya
        //  which is single loan product and uses XLSX for receivable documentation.
        const getReceivableDocumentationUploadDestination = ({ creditFacilitySlug, filename, }) => {
            const invoiceArtifacts = artifacts.filter((artifact) => artifact.type === 'receivableInvoice' &&
                artifact.creditFacilitySlug === creditFacilitySlug);
            // TODO(jon): this assumption will not be correct for multi loan product.
            //  The UI functionality will be hidden behind LD flag showReceivableDocumentationUpload
            //  (enabled only for non-MLP customers) until we fix this assumption.
            (0, common_1.finleyAssert)(invoiceArtifacts.length === 1, `${prefix}: Found ${invoiceArtifacts.length} receivableInvoice for [creditFacilitySlug:${creditFacilitySlug}], expected 1.`);
            const invoiceArtifact = invoiceArtifacts[0];
            const rawDocumentationArtifacts = _getRecursiveInputs(invoiceArtifact.id)
                .map((artifactId) => _getArtifactById(artifactId))
                .filter(({ type }) => type === 'rawReceivableDocumentation');
            (0, common_1.finleyAssert)(rawDocumentationArtifacts.length === 1, `${prefix}: Found ${rawDocumentationArtifacts.length} rawReceivableDocumentation for [creditFacilitySlug:${creditFacilitySlug}], expected 1.`);
            const documentationArtifact = rawDocumentationArtifacts[0];
            let baseFilename;
            let extension;
            const extensionSeparatorIndex = filename.lastIndexOf('.');
            if (extensionSeparatorIndex === -1) {
                baseFilename = filename;
                extension = '';
            }
            else {
                baseFilename = filename.slice(0, extensionSeparatorIndex);
                extension = filename.slice(extensionSeparatorIndex);
            }
            // TODO(jon): this assumption will be incorrect for facilities that care about filename (i.e.,
            //  facilities that expect PDF receivable documentation). The trailing slash is used for
            //  xlsx documentation where any file uploaded with that prefix will be treated as the latest
            //  docs. I.e., Atalaya.
            (0, common_1.finleyAssert)(documentationArtifact.externalFilename?.endsWith('/'), `Expected documentation artifact [artifactId: ${documentationArtifact.id}] externalFilename to end with '/'`);
            const finleyKey = `${documentationArtifact.externalFilename}${baseFilename}-${new Date().toISOString()}${extension}`;
            return { finleyKey };
        };
        const getReceivableInvoiceArtifact = ({ rawDocumentationArtifactId, }) => {
            // asset verification graph starts from `rawReceivableDocumentation` and ends in receivableInvoice
            // LP and CF are configured in receivableInvoice node.
            // this helper returns receivableInvoice artifact given rawReceivableDocumentation node id.
            // ensure that given artifactId is of type rawReceivableDocumentation
            const artifact = _getArtifactById(rawDocumentationArtifactId);
            (0, common_1.finleyAssert)(artifact.type === 'rawReceivableDocumentation', `${prefix}: [artifactId:${rawDocumentationArtifactId}] is not of type rawReceivableDocumentation`);
            // walk the graph to find receivableInvoice node.
            const receivableInvoiceArtifacts = _getRecursiveOutputs(rawDocumentationArtifactId)
                .map((artifactId) => _getArtifactById(artifactId))
                .filter(({ type }) => type === 'receivableInvoice');
            // assumption here is that rawReceivableDocumentation will generate only 1 receivableInvoice node.
            (0, common_1.finleyAssert)(receivableInvoiceArtifacts.length === 1, `${prefix}: found [${receivableInvoiceArtifacts.length}] receivableInvoice nodes for [rawDocumentationArtifactId:${rawDocumentationArtifactId}]`);
            const receivableInvoiceArtifact = receivableInvoiceArtifacts[0];
            // auth check
            // caller can see this artifact
            // only if they have  access to the creditFacilitySlug.
            (0, common_1.finleyAssert)(receivableInvoiceArtifact.creditFacilitySlug, `${prefix}: creditFacilitySlug is required for [artifactId:${receivableInvoiceArtifact.id}]`);
            assertCreditFacilityAccess(receivableInvoiceArtifact.creditFacilitySlug);
            return receivableInvoiceArtifact;
        };
        const getGeneratedLoanTapeArtifact = ({ loanProductSlug, creditFacilitySlug, }) => {
            // find generatedLoanTape node for matching params.
            let matchingArtifacts = _getGeneratedLoanTapeArtifacts({
                loanProductSlug,
            });
            if (matchingArtifacts.length > 1) {
                // if we found multiple generated loan tapes, we must find the one
                // that matches the creditFacilitySlug.
                matchingArtifacts = matchingArtifacts.filter((artifact) => {
                    return artifact.creditFacilitySlug === creditFacilitySlug;
                });
            }
            // we must find only 1 matching artifact
            (0, common_1.finleyAssert)(matchingArtifacts.length === 1, `${prefix}: found [multiple:${matchingArtifacts.length}] generatedLoanTape artifacts for [loanProductSlug:${loanProductSlug}] and [creditFacilitySlug:${creditFacilitySlug}]`);
            return matchingArtifacts[0];
        };
        /**
         * finds generatedLoanTape configuration, and returns TapeDefinition.
         */
        const getGeneratedLoanTapeDefinitionAndConfig = ({ loanProductSlug, creditFacilitySlug, }) => {
            const generatedLoanTapeArtifact = getGeneratedLoanTapeArtifact({
                loanProductSlug,
                creditFacilitySlug,
            });
            const instanceBuilder = (0, tapeBuilder_1.createTapeBuilder)(artifacts);
            const tapeDefinitionResult = instanceBuilder.generateTapeDefinition(generatedLoanTapeArtifact.id);
            (0, common_1.finleyAssert)(tapeDefinitionResult.isValid, `${prefix}: failed to generate tape definition for [loanProductSlug:${loanProductSlug}] and [creditFacilitySlug:${creditFacilitySlug}]`);
            const tapeDefinition = tapeDefinitionResult.data;
            (0, common_1.finleyAssert)(tapeDefinition, `${prefix}: tapeDefinition was null for generated loan tape artifact [loanProductSlug:${loanProductSlug}] and [creditFacilitySlug:${creditFacilitySlug}]`);
            return {
                tapeDefinition,
                config: instanceBuilder.getGeneratedLoanTapeConfig(generatedLoanTapeArtifact.id),
            };
        };
        const getLPIdsforCreditFacility = ({ creditFacilitySlug, }) => {
            assertCreditFacilityAccess(creditFacilitySlug);
            // assignedLoanTape configures both CF and LP.
            const lpIds = artifacts
                .filter((artifact) => {
                return (artifact.type === 'assignedLoanTape' &&
                    artifact.creditFacilitySlug === creditFacilitySlug);
            })
                .map((artifact) => {
                (0, common_1.finleyAssert)(artifact.loanProductSlug !== null, `${prefix}: loanProductSlug is required for [artifactType:${artifact.type}]`);
                return lpSlugToId(artifact.loanProductSlug);
            });
            return lpIds;
        };
        const updateLoanProductMechanics = ({ orgSlug, creditFacilityId, loanProductMechanics, multiLoanProductConcentrationLimits, addRemoveLPs, }) => {
            const creditFacilitySlug = cfIdToSlug(creditFacilityId);
            const errors = [];
            const lpIdsPerTapeBuilder = getLPIdsforCreditFacility({
                creditFacilitySlug,
            });
            const newLoanProductMechanics = loanProductMechanics.filter(({ _id_ }) => {
                // remove any loanProducts not configured in the graph
                // Note: we go by ids here, because the graph will not have slugs for
                // unreferenced loanProduct Ids.
                if (!lpIdsPerTapeBuilder.includes(_id_)) {
                    if (addRemoveLPs) {
                        errors.push(`auto-removed [loanProductId:${_id_}]`);
                        return false;
                    }
                    errors.push(`[loanProductId:${_id_}}] is not configured in tape-builder.`);
                }
                return true;
            });
            const lpIdsPerCA = loanProductMechanics.map(({ _id_ }) => _id_);
            // add any loan products missing from ca.
            lpIdsPerTapeBuilder.forEach((loanProductId) => {
                if (!lpIdsPerCA.includes(loanProductId)) {
                    const lpSlug = lpIdToSlug(loanProductId);
                    if (addRemoveLPs) {
                        const loanProductShell = (0, createLoanProductShell_1.createLoanProductShell)({
                            loanProductId,
                            orgSlug,
                        });
                        newLoanProductMechanics.push(loanProductShell);
                        errors.push(`auto-added [loanProductSlug:${lpSlug}].`);
                    }
                    else {
                        errors.push(`[loanProductSlug:${lpSlug}] is not configured in CA.`);
                    }
                }
            });
            // update loanProductMechanics with details from graph.
            const updatedLoanProductMechanics = newLoanProductMechanics.map((lpMechanics) => {
                const loanProductSlug = lpIdToSlug(lpMechanics._id_);
                const { tapeDefinition, config, } = getGeneratedLoanTapeDefinitionAndConfig({
                    loanProductSlug,
                    creditFacilitySlug,
                });
                const { baseFields } = tapeDefinition;
                const { externalReceivableId, signaledAssignmentValueMapping, externalAssignmentField, rawExternalReceivableIdField, } = config;
                // find signaledExternalAssignment for the given creditFacilitySlug
                let signaledExternalAssignment;
                if (signaledAssignmentValueMapping &&
                    signaledAssignmentValueMapping.length) {
                    const signaledExternalAssignmentFound = signaledAssignmentValueMapping.find((v) => v.creditFacilitySlug === creditFacilitySlug);
                    (0, common_1.finleyAssert)(signaledExternalAssignmentFound, `${prefix}: signaledExternalAssignment not found for [creditFacilitySlug:${creditFacilitySlug}]`);
                    (0, common_1.finleyAssert)(externalAssignmentField, 'externalAssignmentField not found');
                    signaledExternalAssignment = {
                        signaledExternalAssignmentField: externalAssignmentField,
                        signaledExternalAssignment: signaledExternalAssignmentFound.fieldValue,
                    };
                }
                const _config_ = {
                    ...lpMechanics._config_,
                    signaledExternalAssignment,
                    rawExternalReceivableIdField: rawExternalReceivableIdField || undefined,
                    baseFieldConfigs: baseFields,
                    externalReceivableIdField: externalReceivableId,
                };
                return {
                    ...lpMechanics,
                    _config_,
                };
            });
            const lpIdsPerTapeBuilderSet = new Set(lpIdsPerTapeBuilder);
            multiLoanProductConcentrationLimits.forEach((mlpcl) => {
                mlpcl.loanProducts.forEach((lp) => {
                    const lpId = lp._id_;
                    if (!lpIdsPerTapeBuilderSet.has(lpId)) {
                        errors.push(`Multi loan product concentration limit ${mlpcl.name} references [loanProductId:${lpId}], but this loan product is not configured in tape-builder.`);
                    }
                });
            });
            return {
                loanProductMechanics: updatedLoanProductMechanics,
                multiLoanProductConcentrationLimits,
                errors,
            };
        };
        const updateCalcAgentMechanics = ({ orgSlug, creditFacilityId, calcAgentMechanics, addRemoveLPs, }) => {
            const { loanProductMechanics, errors } = updateLoanProductMechanics({
                orgSlug,
                creditFacilityId,
                loanProductMechanics: calcAgentMechanics.loanProductMechanics,
                addRemoveLPs,
                multiLoanProductConcentrationLimits: calcAgentMechanics.multiLoanProductConcentrationLimits,
            });
            return {
                calcAgentMechanics: {
                    ...calcAgentMechanics,
                    _artifactDocumentId_: artifactDocumentId,
                    loanProductMechanics,
                },
                errors,
            };
        };
        /**
         * returns Map(cfSlug => Map(rawArtifactSlug => LoanTapeSanitizeConfig[]))
         * where tapeType is one rawLoanTape
         * For now this only deals with loanTapes.
         */
        const getSanitizeDetails = () => {
            // find bbr nodes that caller has access to.
            const bbrNodes = _getBBRNodes().filter(({ creditFacilitySlug }) => scopedToCFSlugs.includes(creditFacilitySlug));
            const cfSlugToSanitizeConfigsMap = new Map();
            bbrNodes.forEach((bbrNode) => {
                const rawArtifactSlugToSanitizeConfigMap = new Map();
                // get all generated loan tape nodes that go as inputs to selected bbrNodes.
                const generatedLoanTapeNodes = _getRecursiveInputs(bbrNode.id)
                    .map(_getArtifactById)
                    .filter(({ type }) => type === 'generatedLoanTape');
                generatedLoanTapeNodes.forEach((generatedLoanTapeNode) => {
                    (0, common_1.finleyAssert)(typeguards_1.typeguards.isTapeBuilderRecordGeneratedLoanTape(generatedLoanTapeNode), `typeguard.isTapeBuilderRecordGeneratedLoanTape failed: [artifactId:${generatedLoanTapeNode.id}]`);
                    const { loanProductSlug, configuration: { rawExternalReceivableIdField, externalReceivableId, }, } = generatedLoanTapeNode;
                    (0, common_1.finleyAssert)(loanProductSlug, `loanProductSlug is required for generatedLoanTape: ${generatedLoanTapeNode.artifactSlug}`);
                    // find all raw tape nodes that go as inputs to selected bbrNodes.
                    const rawLoanTapeInputs = _getRawInputs(generatedLoanTapeNode.id).filter(({ type }) => type === 'rawLoanTape');
                    rawLoanTapeInputs.forEach((rawLoanTape) => {
                        // we are interested in sanitize operations that
                        // are in the path starting from rawLoanTape to generatedLoanTape.
                        // like:
                        // ============================
                        // rawLoanTape => ... => *sanitize*  => ...=> generatedLoanTape
                        // ============================
                        // to find those we will look for intersection of santitize nodes
                        // that are intersection of
                        //  a) inputs to generatedLoanTape
                        //  b) outputs from rawLoanTape.
                        const inputSanitizeNodes = _getRecursiveInputs(generatedLoanTapeNode.id)
                            .map(_getArtifactById)
                            .filter(({ configuration }) => {
                            return typeguards_1.typeguards.isTapeBuilderSanitizeTask(configuration);
                        })
                            .map(({ id }) => id);
                        const outputSanitizeNodes = _getRecursiveOutputs(rawLoanTape.id)
                            .map(_getArtifactById)
                            .filter(({ configuration }) => {
                            return typeguards_1.typeguards.isTapeBuilderSanitizeTask(configuration);
                        })
                            .map(({ id }) => id);
                        const sanitizeNodes = inputSanitizeNodes.filter((id) => outputSanitizeNodes.includes(id));
                        const sanitizeConfigs = sanitizeNodes.map((sanitizeNode) => {
                            const sanitizeArtifact = _getArtifactById(sanitizeNode);
                            const { artifactSlug, configuration } = sanitizeArtifact;
                            (0, common_1.finleyAssert)(typeguards_1.typeguards.isTapeBuilderSanitizeTask(configuration), 'typeguard.isTapeBuilderSanitizeTask failed');
                            const { baseFields } = configuration;
                            return {
                                sanitizeArtifactSlug: artifactSlug,
                                baseFields,
                                loanProductSlug,
                                rawExternalReceivableIdField: rawExternalReceivableIdField || externalReceivableId,
                            };
                        });
                        rawArtifactSlugToSanitizeConfigMap.set(rawLoanTape.artifactSlug, {
                            artifactType: rawLoanTape.type,
                            sanitizeConfigs,
                        });
                    });
                });
                cfSlugToSanitizeConfigsMap.set(bbrNode.creditFacilitySlug, rawArtifactSlugToSanitizeConfigMap);
            });
            return cfSlugToSanitizeConfigsMap;
        };
        const updateSanitizeDetails = (updatedSanitizeConfigs) => {
            const sanitizeDetails = getSanitizeDetails();
            // collect all sanitizeArtifactSlug from the  sanitizeDetails
            const allSanitizeArtifactSlugs = Array.from(sanitizeDetails.values()).flatMap((map) => Array.from(map.values()).flatMap((v) => v.sanitizeConfigs.map((c) => c.sanitizeArtifactSlug)));
            const givenSanitizeArtifactSlugs = updatedSanitizeConfigs.map(({ sanitizeArtifactSlug }) => sanitizeArtifactSlug);
            // ensure that givenSanitizeArtifactSlugs are subset of allSanitizeArtifactSlugs
            const invalidSanitizeArtifactSlugs = givenSanitizeArtifactSlugs.filter((slug) => !allSanitizeArtifactSlugs.includes(slug));
            (0, common_1.finleyAssert)(invalidSanitizeArtifactSlugs.length === 0, `${prefix}: found invalid sanitizeArtifactSlugs [${invalidSanitizeArtifactSlugs.join(',')}]`);
            const updatedArtifacts = artifacts.map((artifactWithSlug) => {
                const updatedBaseFields = updatedSanitizeConfigs.find(({ sanitizeArtifactSlug }) => sanitizeArtifactSlug === artifactWithSlug.artifactSlug)?.baseFields;
                if (updatedBaseFields) {
                    return {
                        ...artifactWithSlug,
                        configuration: {
                            ...artifactWithSlug.configuration,
                            baseFields: updatedBaseFields,
                        },
                    };
                }
                return artifactWithSlug;
            });
            return updatedArtifacts;
        };
        const getBorrowerConfig = () => {
            const borrowerConfigArtifacts = artifacts.filter((artifact) => artifact.type === 'configTape');
            // assumption here is that there will be only 1 config node.
            (0, common_1.finleyAssert)(borrowerConfigArtifacts.length <= 1, `${prefix}: Found ${borrowerConfigArtifacts.length} borrowerConfig, expected 1.`);
            if (borrowerConfigArtifacts.length === 0) {
                return null;
            }
            const config = borrowerConfigArtifacts[0].configuration;
            (0, common_1.finleyAssert)(typeguards_1.typeguards.isTapeBuilderRecordConfigTapeConfiguration(config), 'getBorrowerConfig failed typeguard.isTapeBuilderRecordConfigTapeConfiguration failed');
            return config.borrowerConfig;
        };
        const _resolveCreditFacilityConfig = (creditFacilityConfig) => {
            const { cashBalancesFeedMethodPointer, repaymentTapeMethodPointer, ...rest } = creditFacilityConfig;
            const resolvedConfig = {
                ...rest,
                cashBalancesFeedPointerMethod: cashBalancesFeedMethodPointer
                    ? (0, pointer_methods_1.resolveCashBalanceFeedPointer)(cashBalancesFeedMethodPointer)
                    : null,
                repaymentTapePointerMethod: repaymentTapeMethodPointer
                    ? (0, pointer_methods_1.resolveRepaymentTapePointer)(repaymentTapeMethodPointer)
                    : null,
            };
            return resolvedConfig;
        };
        const getCreditFacilityConfig = (creditFacilitySlug) => {
            const borrowerConfigArtifacts = artifacts.filter((artifact) => artifact.type === 'configTape');
            // assumption here is that there will be only 1 config node.
            (0, common_1.finleyAssert)(borrowerConfigArtifacts.length <= 1, `${prefix}: Found ${borrowerConfigArtifacts.length} borrowerConfig, expected 1.`);
            if (borrowerConfigArtifacts.length === 0) {
                return null;
            }
            const config = borrowerConfigArtifacts[0].configuration;
            (0, common_1.finleyAssert)(typeguards_1.typeguards.isTapeBuilderRecordConfigTapeConfiguration(config), 'getBorrowerConfig failed typeguard.isTapeBuilderRecordConfigTapeConfiguration failed');
            const creditFacilityConfigs = config.creditFacilityConfigs;
            // find matching creditFacilityConfig
            const matchingConfig = creditFacilityConfigs.filter((cfConfig) => cfConfig.creditFacilitySlug === creditFacilitySlug);
            if (matchingConfig.length === 0) {
                // its possible that CF is not configured.
                return null;
            }
            (0, common_1.finleyAssert)(matchingConfig.length === 1, `${prefix}: found [multiple:${matchingConfig.length}] matching configs for [creditFacilitySlug:${creditFacilitySlug}]`);
            const sourceConfig = matchingConfig[0];
            const resolvedConfig = _resolveCreditFacilityConfig(sourceConfig);
            // find the artifact
            return resolvedConfig;
        };
        const getBbplMonitorScopedArtifacts = () => {
            // this function returns only artifacts that caller has access to.
            // to find all artifacts that caller needs, by looking at all end nodes
            // that belong to the creditFacilitySlug that caller has access to.
            // and then return all nodes that are inputs to these end nodes.
            const endNodeIds = artifacts
                .filter(({ id, creditFacilitySlug }) => {
                return (_getOutputs(id).length === 0 &&
                    creditFacilitySlug !== null &&
                    scopedToCFSlugs.includes(creditFacilitySlug));
            })
                .map(({ id }) => id);
            const allInputIds = endNodeIds.flatMap((id) => _getRecursiveInputs(id));
            const scopedArtifactIds = new Set([...allInputIds, ...endNodeIds]);
            // ignore artifacts that are not part of the BBPL pipeline.
            const ignoreArtifactTypes = [
                'rawReceivableDocumentation',
                'receivableInvoice',
                'configTape',
            ];
            return artifacts.filter(({ id, type }) => scopedArtifactIds.has(id) && !ignoreArtifactTypes.includes(type));
        };
        const serializeForBBPLMonitor = () => {
            const monitorArtifacts = getBbplMonitorScopedArtifacts().map((artifact) => {
                const { id, artifactSlug, type, configuration } = artifact;
                return {
                    id,
                    artifactSlug,
                    type,
                    configuration,
                };
            });
            const monitorDependencies = dependencies.map(({ inputArtifactId, outputArtifactId }) => {
                return {
                    source: inputArtifactId,
                    target: outputArtifactId,
                };
            });
            return JSON.stringify({
                monitorArtifacts,
                monitorDependencies,
            });
        };
        return {
            // access control
            limitScopeToCFSlugs,
            getBbplMonitorScopedArtifacts,
            // generic
            getArtifactDocumentId: () => artifactDocumentId,
            getArtifactDocumentVersion: () => version,
            cfIdToSlug,
            cfSlugToId,
            lpIdToSlug,
            lpSlugToId,
            getCFSlugs,
            getCFIds,
            getLPSlugs,
            getLPIds,
            hasRepaymentTape,
            toArtifact,
            getLPIdsforCreditFacility,
            getInputArtifacts,
            // asset verification
            maybeGetAssetVerificationConfig,
            getReceivableDocumentationUploadDestination,
            getReceivableInvoiceArtifact,
            getAssetVerificationConfigsForCF,
            // update mechanics.
            updateLoanProductMechanics,
            updateCalcAgentMechanics,
            // get and set baseFields for sanitize nodes.
            getSanitizeDetails,
            updateSanitizeDetails,
            // fetch borrower config.
            getBorrowerConfig,
            getCreditFacilityConfig,
            // serialize/deserialize
            serializeForBBPLMonitor,
        };
    };
})(DbGraphUtils = exports.DbGraphUtils || (exports.DbGraphUtils = {}));
//# sourceMappingURL=dbGraphUtils.js.map